import CountryInfo from '../country-info.js';

// We only need to read this cookie.
export const LOCALISATION_COOKIE = {
    name: 'DESIGNSPARK-LOCALE'
};

// We write this cookie with a lifetime of a month.
export const SKIP_COOKIE = {
    name: 'DESIGNSPARK-SKIP-LOCATION-140219',
    lifetime: 2592000
};

export default class GeoLocationNotice
{
    /**
     * @param {Element} context
     * @param {string} currentLanguage
     * @param {Array} availableLanguages
     * @param {String} url
     */
    constructor (context, currentLanguage, availableLanguages, url) {
        /**
         * @type {Element}
         */
        this.context = context;

        /**
         * @type {Array}
         */
        this.availableLanguages = availableLanguages;

        /**
         * @type {String}
         */
        this.url = url;

        try {
            this.getCountryInfo()
                .then(inputCountryCode => {
                    const countryCode = inputCountryCode.toLowerCase();

                    const country = CountryInfo[countryCode.toUpperCase()];

                    if (typeof country === "undefined") {
                        throw new Error("Input country code not recognised. Abort.");
                    }

                    const name = country.Country;
                    const languages = this.getLanguagesForCountry(country.Languages, countryCode);
                    const preferredLanguage = this.guessPreferredLanguage(languages);

                    if (preferredLanguage) {
                        return {language: preferredLanguage, country: name};
                    }

                    throw new Error('Unable to determine language');
                })
                .then(({language, country}) => this.getNotice(language, country))
                .then((notice) => {
                    this.context.innerHTML = notice;
                    document.cookie = `${SKIP_COOKIE.name}=true;secure;max-age=${SKIP_COOKIE.lifetime}`;
                });
        } catch (error) {
            console.error("Geocoding failed with an error.", error);
        }
    }

    /**
     * Computes the list of languages spoken in the given country.
     *
     * NOTE: Returns DS-friendly language names, not necessarily ISO language names.
     *
     * @param {array} inputLanguages
     * @param {string} inputCountryCode
     * @returns {Array}
     */
    getLanguagesForCountry(inputLanguages, inputCountryCode) {
        const countryCode = inputCountryCode.toLowerCase();

        if (['ca', 'us', 'ie'].includes(countryCode)) {
            return ['en'];
        }

        if (countryCode === 'jp') {
            return ['jp'];
        }

        if (countryCode === 'cn') {
            return ['cn'];
        }

        return inputLanguages.split(',')
            .map(spokenLanguage => spokenLanguage.split('-').shift());
    }

    /**
     * Make a request to get the country info from the user
     *
     * @returns {$.Deferred}
     */
    getCountryInfo() {
        const geoLocatedCountry = this.getCookieValue(LOCALISATION_COOKIE.name);
        if (geoLocatedCountry) {
            return $.Deferred().resolve(geoLocatedCountry.toLowerCase()).promise();
        }
        return $.Deferred().reject(new Error('Invalid cookie data')).promise();
    }

    /**
     * Request the notice to display the user sending the determined language & country from the users position
     *
     * @param {String} language
     * @param {String} country
     *
     * @returns {$.Deferred}
     */
    getNotice(language, country) {
        return $.get(this.url, { language: language, country: country });
    }

    /**
     * Get the first supported language from the countries spoken languages
     *
     * @param {Array} languages
     *
     * @returns {string|boolean}
     */
    guessPreferredLanguage(languages) {
        // Intersect the list of available languages on DesignSpark with all languages spoken in this country.
        const allowed = languages.filter((lang) => {
            return this.availableLanguages.includes(lang);
        });

        // No languages allowed? This means we're unlikely to support this language on the site,
        // or that the language codes don't match for some reason (e.g. the Franco-Provencal language "frp-FR" is an exception)
        if (allowed.length < 1) {
            return false;
        }

        // Return the first language in the allowed languages to be the one we'll suggest to the user.
        return allowed.shift();
    }

    /**
     * Reads the value of a cookie,
     *
     * NOTE: Taken verbatim from https://stackoverflow.com/a/25490531.
     * @param {string} name
     * @returns {string}
     */
    getCookieValue(name) {
        let matches = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
        return matches ? matches.pop() : '';
    }
}

// The user has been geolocated by the server, let's  compare their location with their language
// and if they don't match we'll show the language switch notice.
if (document.cookie.indexOf(LOCALISATION_COOKIE.name) > -1
    && document.cookie.indexOf(SKIP_COOKIE.name) < 0
) {
    $('.js-geo-notice').each(function() {
        const url = this.dataset.url;
        const currentLanguage = this.dataset.language;
        const availableLanguages = this.dataset.languages.split(',');
        new GeoLocationNotice(this, currentLanguage, availableLanguages, url);
    });
}



// WEBPACK FOOTER //
// ./src/js/components/geo-location-notice.js